import { Alert, Anchor, Button, Group, Loader, Stack, Table, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { api, podatki, type Shema } from '../api/odjemalec';
import { sporocilo } from '../api/napaka';
import { odpriPdf } from '../api/pdf';
import { datum, denar } from '../oblika/oblika';
import { Kartica } from '../postavitev/Podatki';
import { Oznaka } from '../strani/PregledRacunov';
import { nazivVozila, stanjeVozila } from '../vozila/vozilo';
import type { Vrsta } from './vrste';

type Dokument = Shema['OsebaDokument'];

/** Toliko vrstic je vidnih, dokler uporabnik ne izbere »Pokaži vse«. */
const ZACETNO = 10;

const NAZIV: Record<NonNullable<Dokument['vrsta']>, string> = {
  RACUN: 'Račun',
  PREDRACUN: 'Predračun',
  POSREDNISKA: 'Posredniška',
  PREIZKUSNJA: 'Preizkušnja',
};

/**
 * Dokumenti in vozila osebe (pod podatki na podrobnostih osebe): računi, predračuni, posredniške
 * pogodbe in preizkušnje ter kupljena, prodana ali uporabljena vozila, vse z enim klicem na strežnik.
 */
export function PovezaveOsebe({ vrsta, id }: { vrsta: Vrsta; id: number }) {
  const poizvedba = useQuery({
    queryKey: ['oseba', vrsta.kljuc, id, 'povezave'],
    queryFn: ({ signal }) =>
      podatki(api.GET('/api/osebe/{vrsta}/{id}/povezave', { signal, params: { path: { vrsta: vrsta.kljuc, id } } })),
  });
  const p = poizvedba.data;
  const stanje = (otroci: (p: Shema['OsebaPovezave']) => ReactNode) =>
    p ? (
      otroci(p)
    ) : poizvedba.error ? (
      <Alert color="red" variant="light" role="alert">
        {sporocilo(poizvedba.error)}
      </Alert>
    ) : (
      <Loader size="sm" />
    );
  return (
    <Stack gap="md" mt="md">
      <Kartica naslov={naslov('Dokumenti', p?.dokumenti?.length, p?.vecDokumentov)}>
        {stanje((p) => (
          <Dokumenti dokumenti={p.dokumenti ?? []} omejeno={!!p.vecDokumentov} />
        ))}
      </Kartica>
      <Kartica naslov={naslov('Vozila', p?.vozila?.length, p?.vecVozil)}>
        {stanje((p) => (
          <Vozila vozila={p.vozila ?? []} omejeno={!!p.vecVozil} />
        ))}
      </Kartica>
    </Stack>
  );
}

/** »Dokumenti (12)«; strežnik vrne največ 200 najnovejših, zato pri omejenem seznamu »(200+)«. */
function naslov(osnova: string, stevilo?: number, omejeno?: boolean) {
  return stevilo ? `${osnova} (${stevilo}${omejeno ? '+' : ''})` : osnova;
}

/** Prvih {@link ZACETNO} vrstic, z »Pokaži vse« vse (ki jih je vrnil strežnik). */
function useSkrajsano<T>(seznam: T[]) {
  const [vse, setVse] = useState(false);
  return { prikazani: vse ? seznam : seznam.slice(0, ZACETNO), vse, pokaziVse: () => setVse(true) };
}

function Noga({
  skupaj,
  vse,
  pokaziVse,
  omejeno,
}: {
  skupaj: number;
  vse: boolean;
  pokaziVse: () => void;
  omejeno: boolean;
}) {
  if (!vse && skupaj > ZACETNO) {
    return (
      <Button variant="subtle" size="sm" mt="xs" px={0} onClick={pokaziVse}>
        Pokaži vse ({skupaj}
        {omejeno ? '+' : ''})
      </Button>
    );
  }
  return omejeno ? (
    <Text size="xs" c="dimmed" mt="xs">
      Prikazanih je zadnjih {skupaj}.
    </Text>
  ) : null;
}

function Prazno({ children }: { children: ReactNode }) {
  return (
    <Text size="sm" c="dimmed">
      {children}
    </Text>
  );
}

/** Račun in predračun odpreta podrobnosti, posredniška in preizkušnja PDF. */
function Dokumenti({ dokumenti, omejeno }: { dokumenti: Dokument[]; omejeno: boolean }) {
  const { prikazani, vse, pokaziVse } = useSkrajsano(dokumenti);
  const [dela, setDela] = useState<string | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);
  if (dokumenti.length === 0) {
    return <Prazno>Oseba nima dokumentov.</Prazno>;
  }
  const pdf = async (d: Dokument) => {
    const kljuc = `${d.vrsta}-${d.id}`;
    setNapaka(null);
    setDela(kljuc);
    try {
      await odpriPdf(
        d.vrsta === 'PREIZKUSNJA'
          ? api.GET('/api/preizkusnje/{id}/pdf', { params: { path: { id: d.id! } }, parseAs: 'blob' })
          : api.GET('/api/vozila/{id}/posredniska/pdf', { params: { path: { id: d.voziloId! } }, parseAs: 'blob' }),
      );
    } catch (e) {
      setNapaka(sporocilo(e));
    } finally {
      setDela(null);
    }
  };
  const povezava = (d: Dokument) => {
    const besedilo = `${NAZIV[d.vrsta!]} ${d.stevilka ?? ''}`.trim();
    if (d.vrsta === 'RACUN' || d.vrsta === 'PREDRACUN') {
      return (
        <Anchor component={Link} to={`/${d.vrsta === 'RACUN' ? 'racuni' : 'predracuni'}/${d.id}`} fw={600}>
          {besedilo}
        </Anchor>
      );
    }
    // posredniška brez vozila nima PDF-ja (natisne se prek vozila)
    if (d.vrsta === 'POSREDNISKA' && !d.voziloId) {
      return (
        <Text fw={600} size="sm" span>
          {besedilo}
        </Text>
      );
    }
    return (
      <>
        <Anchor component="button" type="button" fw={600} size="sm" onClick={() => void pdf(d)}>
          {besedilo}
        </Anchor>
        {dela === `${d.vrsta}-${d.id}` && <Loader size={12} />}
      </>
    );
  };
  return (
    <>
      {napaka && (
        <Alert color="red" variant="light" mb="sm" role="alert">
          {napaka}
        </Alert>
      )}
      <Table.ScrollContainer minWidth={480}>
        <Table className="stevilke" verticalSpacing={6}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Dokument</Table.Th>
              <Table.Th>Datum</Table.Th>
              <Table.Th>Vsebina</Table.Th>
              <Table.Th ta="right">Znesek</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {prikazani.map((d) => (
              <Table.Tr key={`${d.vrsta}-${d.id}`}>
                <Table.Td>
                  <Group gap={6} wrap="nowrap" className="brez-preloma">
                    {povezava(d)}
                    {d.oznaka && (
                      <Oznaka barva={d.oznaka === 'storniran' ? 'red' : d.oznaka === 'dobropis' ? 'orange' : 'gray'}>
                        {d.oznaka}
                      </Oznaka>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td className="brez-preloma">{datum(d.datum)}</Table.Td>
                <Table.Td>
                  <Text size="sm" lineClamp={2}>
                    {d.opis}
                  </Text>
                </Table.Td>
                <Table.Td ta="right" className="brez-preloma">
                  {denar(d.znesek)}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <Noga skupaj={dokumenti.length} vse={vse} pokaziVse={pokaziVse} omejeno={omejeno} />
    </>
  );
}

function Vozila({ vozila, omejeno }: { vozila: Shema['OsebaVozilo'][]; omejeno: boolean }) {
  const { prikazani, vse, pokaziVse } = useSkrajsano(vozila);
  if (vozila.length === 0) {
    return <Prazno>Oseba nima vozil.</Prazno>;
  }
  return (
    <>
      <Table.ScrollContainer minWidth={480}>
        <Table verticalSpacing={6}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Vozilo</Table.Th>
              <Table.Th>Reg. št.</Table.Th>
              <Table.Th>VIN</Table.Th>
              <Table.Th>Povezava</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {prikazani.map(({ vozilo: v, povezava, datum: dan }) => {
              const stanje = v && stanjeVozila(v);
              return (
                <Table.Tr key={v?.id}>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      <Anchor component={Link} to={`/vozila/${v?.id}`} fw={600} className="brez-preloma">
                        {nazivVozila(v)}
                      </Anchor>
                      {stanje && <Oznaka barva={stanje.barva}>{stanje.naziv}</Oznaka>}
                    </Group>
                  </Table.Td>
                  <Table.Td className="brez-preloma">{v?.regSt}</Table.Td>
                  <Table.Td className="brez-preloma">{v?.vin}</Table.Td>
                  <Table.Td className="brez-preloma">
                    {povezava}
                    {dan && (
                      <Text span size="sm" c="dimmed">
                        {' '}
                        {datum(dan)}
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <Noga skupaj={vozila.length} vse={vse} pokaziVse={pokaziVse} omejeno={omejeno} />
    </>
  );
}
